import moment from 'moment-timezone';
import type { KpiTrend } from '~containers/analytics/kpis/types';
import type {
    ContestBlockType,
    ContestEntryOrigin,
    ContestPhotoReviewStatus,
    ContestStats,
} from '../../../stores/contest';
import { ENTRY_PATH_LABELS } from '../entries-tab/labels';

export const ORIGIN_LABELS: Record<ContestEntryOrigin, string> = {
    ...ENTRY_PATH_LABELS,
    in_store: 'In-store purchase',
};

/** Each reason the server refuses an attempt, as the team reads it. */
export const REFUSAL_LABELS: Record<string, string> = {
    closed: 'Contest closed',
    participant_required: 'Entry before signing up',
    province: 'Province not eligible',
    invalid_phone_number: 'Invalid phone number',
    invalid_email: 'Invalid email',
    invalid_postal_code: 'Invalid postal code',
    policies_not_accepted: 'Rules not accepted',
    invalid_consents: 'Invalid consents',
    captcha_refused: 'Captcha refused',
    otp_rate_limited: 'Too many codes requested by SMS',
    otp_invalid_code: 'Wrong SMS code',
    otp_attempts_exceeded: 'Too many wrong SMS codes',
    invalid_code: 'Invalid proof-of-purchase code',
    code_already_used: 'Code already used',
    daily_cap: 'Daily cap reached',
    total_cap: 'Contest cap reached',
    skill_failed: 'Wrong skill-testing answer',
    skill_expired: 'Skill-testing question expired',
    refusals_rate_limited: 'Too many refused codes',
    photo_type_not_allowed: 'Photo format not allowed',
    photo_too_large: 'Photo too large',
    photo_not_uploaded: 'Photo not uploaded',
    photo_already_submitted: 'Photo already submitted',
    messages_rate_limited: 'Too many contact messages',
};

/** Refusals a script or an abuser produces, rather than a confused participant. */
export const ABUSE_REFUSALS = [
    'code_already_used',
    'captcha_refused',
    'otp_rate_limited',
    'otp_attempts_exceeded',
    'refusals_rate_limited',
    'messages_rate_limited',
];

export const PHOTO_REVIEW_LABELS: Record<ContestPhotoReviewStatus, string> = {
    pending: 'Pending',
    manual_review: 'Manual review',
    approved: 'Approved',
    rejected: 'Rejected',
};

/** How many refusal reasons the chart names; the rest add up under Others. */
export const SHOWN_REFUSAL_COUNT = 8;

/** A share of a whole, null when there is no whole to share. */
export function share(part: number, whole: number): number | null {
    return whole > 0 ? part / whole : null;
}

export function entriesOn(stats: ContestStats, day: string): number {
    return stats.days.find((row) => row.day === day)?.entries ?? 0;
}

/** The contest day before `day`, both as `YYYY-MM-DD`. */
export function dayBefore(day: string): string {
    return moment.utc(day).subtract(1, 'day').format('YYYY-MM-DD');
}

/** Today's entries against yesterday's. */
export function todayTrend(stats: ContestStats): KpiTrend {
    const delta =
        entriesOn(stats, stats.today) -
        entriesOn(stats, dayBefore(stats.today));
    return {
        delta,
        unit: 'count',
        direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
        comparisonLabel: 'vs yesterday',
    };
}

function pluralDays(days: number): string {
    return days === 1 ? '1 day' : `${days} days`;
}

/** Midnights crossed from one instant to the other, in the time zone. */
function calendarDays(from: Date, to: Date, timeZone: string): number {
    return moment
        .tz(to, timeZone)
        .startOf('day')
        .diff(moment.tz(from, timeZone).startOf('day'), 'days');
}

/**
 * Where the contest stands in its period, for the headline: the days left
 * while it runs, counting today, and a status before and after. Days are
 * calendar days in the contest's time zone, as on the daily chart.
 */
export function periodStatus(
    contest: { startAt: string; endAt: string },
    now: Date,
    timeZone: string
): { value: number | string; title: string } {
    const start = new Date(contest.startAt);
    const end = new Date(contest.endAt);
    if (now < start) {
        const days = calendarDays(now, start, timeZone);
        return days === 0
            ? { title: 'Starts', value: 'Today' }
            : { title: 'Starts in', value: pluralDays(days) };
    }
    if (now >= end) {
        return { title: 'Contest', value: 'Ended' };
    }
    const lastDay = new Date(end.getTime() - 1);
    return {
        title: 'Days left',
        value: calendarDays(now, lastDay, timeZone) + 1,
    };
}

/**
 * The sections that rest on one block type. Each shows while the contest shows
 * that block, and stays once it holds figures, so turning the block off hides
 * nothing already counted.
 */
export function blockSections(
    stats: ContestStats
): {
    referral: boolean;
    codes: boolean;
    messages: boolean;
} {
    function shows(type: ContestBlockType): boolean {
        return stats.blockTypes.includes(type);
    }
    const { codes } = stats;
    return {
        referral: shows('referral') || stats.referral.referredParticipants > 0,
        codes:
            shows('code_entry') ||
            codes.accepted + codes.invalid + codes.alreadyUsed > 0,
        messages: shows('contact_form') || stats.messages.received > 0,
    };
}

/** The most frequent reasons, then the rest added up under Others. */
export function topRefusals(
    refusals: ContestStats['refusals']
): { key: string; label: string; value: number }[] {
    const shown = refusals.slice(0, SHOWN_REFUSAL_COUNT).map((row) => ({
        key: row.reason,
        label: REFUSAL_LABELS[row.reason] ?? row.reason,
        value: row.count,
    }));
    const others = refusals
        .slice(SHOWN_REFUSAL_COUNT)
        .reduce((total, row) => total + row.count, 0);
    return others > 0
        ? [...shown, { key: 'others', label: 'Others', value: others }]
        : shown;
}

/** Codes refused, of every code typed. */
export function codeRefusalRate(codes: ContestStats['codes']): number | null {
    const refused = codes.invalid + codes.alreadyUsed;
    return share(refused, refused + codes.accepted);
}

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Entries per weekday (Monday first) and hour, every slot present. */
export function weekHourGrid(weekHours: ContestStats['weekHours']): number[][] {
    const grid = WEEKDAY_LABELS.map(() => Array<number>(24).fill(0));
    for (const slot of weekHours) {
        grid[slot.weekday - 1][slot.hour] += slot.entries;
    }
    return grid;
}
